import { useState } from "react";
import ProtectedImage from "@/components/ProtectedImage";

// Supported metals (5)
const METALS = ["Gold", "Silver", "Platinum", "Palladium", "Rhodium"];

// Standard (intraday) chart types (5)
const STANDARD_CHART_TYPES = [
  { id: "3day", name: "3-Day Chart", description: "Full 3-day price overlay with all three lines visible", supportsCurrency: true, category: "standard" },
  { id: "24h", name: "24h Live Chart", description: "Single day plot with tighter scale and previous close reference lines", supportsCurrency: true, category: "standard" },
  { id: "24h_simple", name: "24h Simple Chart", description: "Simplified 24h chart with clean design and single close reference", supportsCurrency: true, category: "standard" },
  { id: "24h_compact", name: "24h Compact Chart", description: "Embeddable compact 24h layout (light theme only)", supportsCurrency: true, category: "standard", lightOnly: true },
  { id: "ny_hours", name: "NY Hours Chart", description: "NY trading hours only (8 AM - 5 PM ET) - USD only", supportsCurrency: false, category: "standard" }
];

// Historical chart types (11)
const HISTORICAL_CHART_TYPES = [
  { id: "30d", name: "30 Day", description: "30-day price history with 4-hour data points", supportsCurrency: true, category: "historical" },
  { id: "60d", name: "60 Day", description: "60-day price history with 8-hour data points", supportsCurrency: true, category: "historical" },
  { id: "3m", name: "3 Month", description: "3-month price history with daily data points", supportsCurrency: true, category: "historical" },
  { id: "6m", name: "6 Month", description: "6-month price history with daily data points", supportsCurrency: true, category: "historical" },
  { id: "1y", name: "1 Year", description: "1-year price history with monthly labels", supportsCurrency: true, category: "historical" },
  { id: "5y", name: "5 Year", description: "5-year price history with yearly labels", supportsCurrency: true, category: "historical" },
  { id: "10y", name: "10 Year", description: "10-year price history", supportsCurrency: true, category: "historical" },
  { id: "15y", name: "15 Year", description: "15-year price history", supportsCurrency: true, category: "historical" },
  { id: "20y", name: "20 Year", description: "20-year price history", supportsCurrency: true, category: "historical" },
  { id: "30y", name: "30 Year", description: "30-year price history", supportsCurrency: true, category: "historical" },
  { id: "all", name: "All Time", description: "Complete history since 1973", supportsCurrency: true, category: "historical" }
];

// All chart types
const ALL_CHART_TYPES = [...STANDARD_CHART_TYPES, ...HISTORICAL_CHART_TYPES];

// Supported currencies
const CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "MXN", name: "Mexican Peso", symbol: "MX$" },
  { code: "RUB", name: "Russian Ruble", symbol: "₽" },
  { code: "ZAR", name: "South African Rand", symbol: "R" }
];

// Chart template specifications
const CHART_TEMPLATES = [
  {
    id: "dark-v1",
    name: "Dark Theme",
    version: "1.0",
    status: "LOCKED",
    file: "chart_generator.py",
    description: "Deep dark background with neon price lines. Professional financial chart aesthetic.",
    theme: "dark",
    dimensions: { large: "1260 × 800", medium: "800 × 500" },
    colors: {
      background: { value: "#0A0C10", rgb: "10, 12, 16", label: "Background" },
      today: { value: "#00FF88", rgb: "0, 255, 136", label: "Today (Green)" },
      yesterday: { value: "#FF3B30", rgb: "255, 59, 48", label: "Yesterday (Red)" },
      dayBefore: { value: "#00D4FF", rgb: "0, 212, 255", label: "Day Before (Cyan)" },
      title: { value: "#38BDF8", rgb: "56, 189, 248", label: "Title Blue" },
      grid: { value: "#1E2328", rgb: "30, 35, 40", label: "Grid Lines" },
      legend: { value: "#0F1114", rgb: "15, 17, 20", label: "Legend Background" },
    },
    features: [
      "Neon price lines",
      "Borderless floating legend",
      "Circle indicators in legend",
      "Current price dot with glow",
      "Last Close dashed line",
      "High/Low inside chart",
      "Trend arrows (▲▼)",
    ],
    sessions: [
      { name: "Sydney", color: "#0EA5E9" },
      { name: "Shanghai", color: "#14B8A6" },
      { name: "London", color: "#6366F1" },
      { name: "New York", color: "#F59E0B" }
    ]
  },
  {
    id: "light-v1",
    name: "Light Theme",
    version: "1.1",
    status: "LOCKED",
    file: "chart_generator_light.py",
    description: "Clean white background with subtle session shading. Darker lines for readability.",
    theme: "light",
    dimensions: { large: "1260 × 800", medium: "800 × 500" },
    colors: {
      background: { value: "#FAFAFC", rgb: "250, 250, 252", label: "Background" },
      today: { value: "#059669", rgb: "5, 150, 105", label: "Today (Green)" },
      yesterday: { value: "#DC2626", rgb: "220, 38, 38", label: "Yesterday (Red)" },
      dayBefore: { value: "#0891B2", rgb: "8, 145, 178", label: "Day Before (Cyan)" },
      title: { value: "#648CB4", rgb: "100, 140, 180", label: "Title (Muted Blue)" },
      grid: { value: "#E6E8EC", rgb: "230, 232, 236", label: "Grid Lines" },
      legend: { value: "#FFFFFF", rgb: "255, 255, 255", label: "Legend Background" },
      text: { value: "#475569", rgb: "71, 85, 105", label: "Legend Text" },
    },
    features: [
      "Darker lines for readability",
      "White legend with shadow",
      "Circle indicators in legend",
      "Transparent Last Close label",
      "Very subtle session shading",
      "High/Low inside chart",
      "Trend arrows (▲▼)",
    ],
    sessions: [
      { name: "Sydney", color: "#0EA5E9", opacity: "15%" },
      { name: "Shanghai", color: "#14B8A6", opacity: "15%" },
      { name: "London", color: "#6366F1", opacity: "15%" },
      { name: "New York", color: "#F59E0B", opacity: "18%" }
    ]
  }
];

function ColorSwatch({ color, size = "md" }) {
  const sizeClasses = size === "sm" ? "w-4 h-4" : "w-6 h-6";
  return (
    <div 
      className={`${sizeClasses} rounded border border-zinc-600`}
      style={{ backgroundColor: color }}
      title={color}
    />
  );
}

function ChartPreviewCard({ metal, theme, chartType, filename }) {
  return (
    <div className="bg-zinc-800/30 rounded-lg overflow-hidden border border-zinc-700/50 hover:border-zinc-600 transition-colors">
      <div className="aspect-[1260/800] relative">
        <ProtectedImage
          src={`/api/charts/file/${filename}`}
          alt={`${metal} ${theme} ${chartType}`}
          className="w-full h-full object-contain"
          loadingFallback={
            <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm">
              Loading chart
            </div>
          }
          errorFallback={
            <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm">
              Chart not generated
            </div>
          }
        />
      </div>
      <div className="p-2 border-t border-zinc-700/50">
        <p className="text-xs font-medium text-zinc-300">{metal}</p>
        <p className="text-xs text-zinc-500 font-mono truncate">{filename}</p>
      </div>
    </div>
  );
}

function TemplateSection({ template, selectedCurrency }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleIntradayTypes = STANDARD_CHART_TYPES.filter((chartType) => !chartType.lightOnly || template.theme === "light");
  
  // Generate filenames for this theme, chart type, and currency
  const getFilename = (metal, chartType) => {
    const metalLower = metal.toLowerCase();
    const suffix = template.theme === "light" ? "_light" : "";
    // NY Hours is always USD
    const currency = chartType === "ny_hours" ? "usd" : selectedCurrency.toLowerCase();
    return `${metalLower}_${chartType}_${currency}${suffix}.png`;
  };

  return (
    <div 
      data-testid={`template-section-${template.id}`}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                template.theme === "dark" 
                  ? "bg-zinc-950 border border-zinc-700" 
                  : "bg-zinc-100 border border-zinc-300"
              }`}
            >
              <span className={`text-sm font-bold ${
                template.theme === "dark" ? "text-cyan-400" : "text-slate-600"
              }`}>
                {template.theme === "dark" ? "D" : "L"}
              </span>
            </div>
            <div>
              <h3 className="text-base font-semibold text-zinc-100">{template.name}</h3>
              <p className="text-xs text-zinc-500">v{template.version} • {template.file}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded">
              {template.status}
            </span>
            <svg 
              className={`w-5 h-5 text-zinc-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <p className="mt-2 text-sm text-zinc-400">{template.description}</p>
      </div>

      {/* Standard Chart Previews */}
      <div className="px-4 pb-4 space-y-4">
        <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-700 pb-2">Intraday Charts</h4>
        {visibleIntradayTypes.map((chartType) => (
          <div key={chartType.id}>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{chartType.name}</h4>
              {!chartType.supportsCurrency && (
                <span className="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">USD only</span>
              )}
              {chartType.lightOnly && (
                <span className="px-1.5 py-0.5 text-xs bg-zinc-700/50 text-zinc-300 rounded">Light only</span>
              )}
              <span className="text-xs text-zinc-600">— {chartType.description}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {METALS.map((metal) => (
                <ChartPreviewCard 
                  key={`${metal}-${chartType.id}`}
                  metal={metal}
                  theme={template.theme}
                  chartType={chartType.id}
                  filename={getFilename(metal, chartType.id)}
                />
              ))}
            </div>
          </div>
        ))}
        
        {/* Historical Chart Previews */}
        <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-700 pb-2 pt-4">Historical Charts</h4>
        {HISTORICAL_CHART_TYPES.map((chartType) => (
          <div key={chartType.id}>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{chartType.name}</h4>
              <span className="text-xs text-zinc-600">— {chartType.description}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {METALS.map((metal) => (
                <ChartPreviewCard 
                  key={`${metal}-${chartType.id}`}
                  metal={metal}
                  theme={template.theme}
                  chartType={chartType.id}
                  filename={getFilename(metal, chartType.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-zinc-800 p-4 space-y-4">
          {/* Colors & Sessions Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Colors */}
            <div>
              <h4 className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wide">Color Palette</h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(template.colors).map(([key, color]) => (
                  <div key={key} className="bg-zinc-800/50 rounded p-2 flex items-center gap-2">
                    <ColorSwatch color={color.value} />
                    <div className="min-w-0">
                      <p className="text-xs text-zinc-400 truncate">{color.label}</p>
                      <p className="text-xs font-mono text-zinc-500">{color.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sessions */}
            <div>
              <h4 className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wide">Trading Sessions</h4>
              <div className="flex flex-wrap gap-2">
                {template.sessions.map((session) => (
                  <div key={session.name} className="flex items-center gap-2 px-2 py-1 bg-zinc-800/50 rounded">
                    <ColorSwatch color={session.color} size="sm" />
                    <span className="text-xs text-zinc-300">{session.name}</span>
                    {session.opacity && <span className="text-xs text-zinc-500">({session.opacity})</span>}
                  </div>
                ))}
              </div>
              
              <h4 className="text-xs font-medium text-zinc-400 mb-2 mt-4 uppercase tracking-wide">Features</h4>
              <div className="flex flex-wrap gap-1">
                {template.features.map((feature, idx) => (
                  <span key={idx} className="px-2 py-1 bg-zinc-800/50 rounded text-xs text-zinc-400">
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* API Usage */}
          <div>
            <h4 className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wide">API Usage</h4>
            <div className="bg-zinc-950 rounded p-3 font-mono text-xs overflow-x-auto">
              <span className="text-amber-400">POST</span>{" "}
              <span className="text-zinc-400">/api/charts</span>
              <span className="text-zinc-600"> {"{"}</span>
              <span className="text-zinc-500">"theme":</span>{" "}
              <span className="text-emerald-400">"{template.theme}"</span>
              <span className="text-zinc-600">, </span>
              <span className="text-zinc-500">"metal":</span>{" "}
              <span className="text-emerald-400">"Gold"</span>
              <span className="text-zinc-600">, </span>
              <span className="text-zinc-500">"chart_type":</span>{" "}
              <span className="text-emerald-400">"1y"</span>
              <span className="text-zinc-600">, </span>
              <span className="text-zinc-500">"currency":</span>{" "}
              <span className="text-emerald-400">"{selectedCurrency}"</span>
              <span className="text-zinc-600">{"}"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Templates() {
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  
  // Calculate totals
  const standardChartCount = STANDARD_CHART_TYPES.length;
  const historicalChartCount = HISTORICAL_CHART_TYPES.length;
  const totalChartTypes = standardChartCount + historicalChartCount;
  
  // Base charts per theme (all metals × all chart types)
  const baseChartsPerTheme = METALS.length * totalChartTypes;
  const baseCharts = CHART_TEMPLATES.length * baseChartsPerTheme;
  
  // Currency variations (excluding NY Hours which is USD only)
  const currencySupportedTypes = totalChartTypes - 1; // all except ny_hours
  const currencyVariations = CHART_TEMPLATES.length * METALS.length * currencySupportedTypes * CURRENCIES.length;
  const nyHoursCharts = CHART_TEMPLATES.length * METALS.length; // USD only
  const totalVariations = currencyVariations + nyHoursCharts;
  
  return (
    <main className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="templates-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-100 mb-1">Chart Inventory</h1>
        <p className="text-zinc-400 text-sm">
          {baseCharts} base templates × {CURRENCIES.length} currencies = <span className="text-emerald-400 font-medium">{totalVariations} total variations</span>
        </p>
      </div>

      {/* Currency Filter */}
      <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium text-zinc-300 mb-1">Currency Filter</h3>
            <p className="text-xs text-zinc-500">View charts in different currencies (NY Hours always USD)</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {CURRENCIES.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.code} - {curr.name}
                </option>
              ))}
            </select>
            <span className="text-xs text-zinc-500 hidden md:block">
              Showing: <span className="text-zinc-300 font-medium">{selectedCurrency}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-6">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-zinc-100">{CHART_TEMPLATES.length}</p>
          <p className="text-xs text-zinc-500">Themes</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-zinc-100">{standardChartCount}</p>
          <p className="text-xs text-zinc-500">Intraday</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-cyan-400">{historicalChartCount}</p>
          <p className="text-xs text-zinc-500">Historical</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-zinc-100">{METALS.length}</p>
          <p className="text-xs text-zinc-500">Metals</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-amber-400">{CURRENCIES.length}</p>
          <p className="text-xs text-zinc-500">Currencies</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-emerald-400">{totalVariations.toLocaleString()}</p>
          <p className="text-xs text-zinc-500">Total Variations</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <p className="text-xl font-bold text-zinc-100">~57KB</p>
          <p className="text-xs text-zinc-500">Avg Size</p>
        </div>
      </div>

      {/* Template Sections */}
      <div className="space-y-4">
        {CHART_TEMPLATES.map((template) => (
          <TemplateSection key={template.id} template={template} selectedCurrency={selectedCurrency} />
        ))}
      </div>

      {/* Naming Convention */}
      <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
        <h4 className="text-sm font-medium text-zinc-300 mb-2">File Naming Convention</h4>
        <div className="font-mono text-xs text-zinc-400 space-y-1">
          <p><span className="text-zinc-500"># Pattern:</span> {"{metal}"}_{"{type}"}_{"{currency}"}.png</p>
          <p><span className="text-zinc-500"># With Light Theme:</span> {"{metal}"}_{"{type}"}_{"{currency}"}_light.png</p>
          <p className="mt-2 text-zinc-500"># Examples:</p>
          <p>gold_3day_usd.png, gold_24h_eur_light.png, silver_24h_simple_gbp.png</p>
          <p>gold_24h_compact_usd_light.png</p>
          <p className="mt-2"><span className="text-amber-400">Note:</span> NY Hours charts are always USD: {"{metal}"}_ny_hours_usd.png</p>
        </div>
      </div>

      {/* Currency List */}
      <div className="mt-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
        <h4 className="text-sm font-medium text-zinc-300 mb-3">Supported Currencies</h4>
        <div className="flex flex-wrap gap-2">
          {CURRENCIES.map((curr) => (
            <span 
              key={curr.code}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                selectedCurrency === curr.code 
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" 
                  : "bg-zinc-800/50 text-zinc-400 border border-zinc-700/50"
              }`}
            >
              {curr.symbol} {curr.code}
            </span>
          ))}
        </div>
      </div>
    </main>
  );
}
